<template>
    <div class="sl-main">
        <div class="sl-page sl-page--full">
            <div id="sl-cleaning-product">
                <!-- Page Header -->
                <div class="sl-page__header">
                    <div class="sl-page__title">
                        {{ $t('cleaning_products.page_title') }}
                    </div>
                </div>

                <!-- Page Content -->
                <div class="sl-page__content text-gray-500">
                    <div class="flex items-center mb-2 text-primary-500 text-lg">
                        <span>
                            {{ $t('cleaning_products.filter_by_intended_use') }}
                        </span>

                        <a
                            v-if="(intendedUseFilters.length) > 0 || productTypeFilter"
                            href="#!"
                            class="cursor-pointer ml-3 text-xs text-blue-500 outline-none transition
                                hover:underline hover:text-blue-600 focus:underline focus:text-blue-600"
                            @click.stop="resetFilters()"
                        >
                            {{ $t('cleaning_products.clear_filters') }}
                        </a>
                    </div>

                    <!-- Intended Use Filters -->
                    <div class="mb-4 flex flex-wrap gap-3">
                        <template
                            v-for="(cssClass, tag) in intendedUseTags"
                            :key="tag"
                        >
                            <a
                                v-if="hasArticlesWithTag(tag)"
                                href="#!"
                                class="sl-article-grid-iu-filter"
                                :class="getIntendedUseFilterClass(tag)"
                                @click.stop.prevent="toggleIntendedUseFilter(tag)"
                            >
                                {{ getIntendedUseTagLabel(tag) }}
                            </a>
                        </template>
                    </div>

                    <div class="flex">
                        <!-- Product Filters -->
                        <div v-if="cEntity.country !== 'UA'" class="w-1/6 shrink-0 mr-2">
                            <div class="mb-2 text-primary-500 text-lg">
                                {{ $t('cleaning_products.filter_by_product') }}
                            </div>

                            <div class="-ml-4">
                                <a
                                    v-if="hasArticlesWithTag($const.tags.ALL_CLEAN)"
                                    href="#!"
                                    class="sl-article-grid-product-filter"
                                    :class="{ 'sl-article-grid-product-filter--active': productTypeFilter === $const.tags.ALL_CLEAN }"
                                    @click.prevent.stop="setProductTypeFilter($const.tags.ALL_CLEAN)"
                                >
                                    All Clean
                                </a>

                                <a
                                    v-if="hasArticlesWithTag($const.tags.ALL_IN_ONE)"
                                    href="#!"
                                    class="sl-article-grid-product-filter"
                                    :class="{ 'sl-article-grid-product-filter--active': productTypeFilter === $const.tags.ALL_IN_ONE }"
                                    @click.prevent.stop="setProductTypeFilter($const.tags.ALL_IN_ONE)"
                                >
                                    All-in-One
                                </a>

                                <a
                                    v-if="hasArticlesWithTag($const.tags.CLEANFLEX)"
                                    href="#!"
                                    class="sl-article-grid-product-filter"
                                    :class="{ 'sl-article-grid-product-filter--active': productTypeFilter === $const.tags.CLEANFLEX }"
                                    @click.prevent.stop="setProductTypeFilter($const.tags.CLEANFLEX)"
                                >
                                    CleanFlex
                                </a>

                                <a
                                    v-if="hasArticlesWithTag($const.tags.ONE_STEP_T)"
                                    href="#!"
                                    class="sl-article-grid-product-filter"
                                    :class="{ 'sl-article-grid-product-filter--active': productTypeFilter === $const.tags.ONE_STEP_T }"
                                    @click.prevent.stop="setProductTypeFilter($const.tags.ONE_STEP_T)"
                                >
                                    One Step-T
                                </a>

                                <a
                                    v-if="hasArticlesWithTag($const.tags.STOREFLEX)"
                                    href="#!"
                                    class="sl-article-grid-product-filter"
                                    :class="{ 'sl-article-grid-product-filter--active': productTypeFilter === $const.tags.STOREFLEX }"
                                    @click.prevent.stop="setProductTypeFilter($const.tags.STOREFLEX)"
                                >
                                    StoreFlex
                                </a>

                                <a
                                    v-if="hasArticlesWithTag($const.tags.ENZYME)"
                                    href="#!"
                                    class="sl-article-grid-product-filter"
                                    :class="{ 'sl-article-grid-product-filter--active': productTypeFilter === $const.tags.ENZYME }"
                                    @click.prevent.stop="setProductTypeFilter($const.tags.ENZYME)"
                                >
                                    Enzyme
                                </a>

                                <a
                                    v-if="hasArticlesWithTag($const.tags.FRESH)"
                                    href="#!"
                                    class="sl-article-grid-product-filter"
                                    :class="{ 'sl-article-grid-product-filter--active': productTypeFilter === $const.tags.FRESH }"
                                    @click.prevent.stop="setProductTypeFilter($const.tags.FRESH)"
                                >
                                    Fresh
                                </a>

                                <a
                                    v-if="hasArticlesWithTag($const.tags.UNIFRESH)"
                                    href="#!"
                                    class="sl-article-grid-product-filter"
                                    :class="{ 'sl-article-grid-product-filter--active': productTypeFilter === $const.tags.UNIFRESH }"
                                    @click.prevent.stop="setProductTypeFilter($const.tags.UNIFRESH)"
                                >
                                    Unifresh
                                </a>

                                <a
                                    v-if="hasArticlesWithTag($const.tags.ISOSOL)"
                                    href="#!"
                                    class="sl-article-grid-product-filter"
                                    :class="{ 'sl-article-grid-product-filter--active': productTypeFilter === $const.tags.ISOSOL }"
                                    @click.prevent.stop="setProductTypeFilter($const.tags.ISOSOL)"
                                >
                                    Isosol
                                </a>
                            </div>

                            <!-- Discount does not apply to UA customers -->
                            <template v-if="cEntity.country !== 'UA'">
                                <!-- Discount Rules Explanation -->
                                <div class="mt-4 mb-2 text-primary-500 text-lg">
                                    {{ $t('article_list.discounts.title') }}
                                </div>

                                <p class="mr-6 text-sm">
                                    {{ $t('article_list.discounts.intro') }}
                                </p>

                                <div
                                    class="cursor-pointer flex items-center text-sm mt-2 transition select-none"
                                    :class="{ 'text-primary-500': showOnlyPrepackaged }"
                                    @click="showOnlyPrepackaged = !showOnlyPrepackaged"
                                >
                                    <mdi-icon
                                        :icon="showOnlyPrepackaged ? 'mdiCheck' : 'mdiCheckboxBlankOutline'"
                                        class="w-5 h-5 mr-1"
                                    />

                                    <span>
                                        {{ $t('article_list.discounts.show_boxes_only') }}
                                    </span>
                                </div>

                                <div class="mt-2 mr-6 text-sm">
                                    <div class="flex flex-wrap items-center border-b border-primary-100 w-full">
                                        <div class="pr-2 py-1 w-2/3 text-primary-500">
                                            {{ $t('article_list.discounts.table.condition') }}
                                        </div>

                                        <div class="pl-2 py-1 w-1/3 text-primary-500 text-right">
                                            {{ $t('article_list.discounts.table.discount') }}
                                        </div>
                                    </div>

                                    <div
                                        v-for="rule in discountRules"
                                        :key="rule.i18nNum"
                                        class="flex flex-wrap items-center border-b border-primary-100 w-full"
                                    >
                                        <div class="pr-2 py-1 w-2/3">
                                            {{ $t('article_list.discounts.condition', { num: rule.num }, rule.i18nNum) }}
                                        </div>

                                        <div class="pl-2 py-1 w-1/3 text-right" :class="{ 'text-gray-400': rule.discount === $t('article_list.discounts.no_discount') }">
                                            {{ rule.discount }}
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>

                        <!-- Articles List -->
                        <article-grid
                            :articles="filteredArticles"
                            :filter-badges-callback="getFilterBadgesCallback()"
                            :badges-callback="getBadgesCallback()"
                            class="w-full"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import constants from '@/constants/constants';
import { useArticleGridComposable } from '@/composables/ArticleGridComposable';
import ArticleGrid from '@/components/articles-grid/ArticleGrid.vue';

// ------------------------------------------------------------ COMPOSABLES

const store = useStore();
const { t } = useI18n({ useScope: 'global' });
const {
    intendedUseTags,
    getIntendedUseTagLabel,
    getFilterBadgesCallback,
    getBadgesCallback,
} = useArticleGridComposable();

// ------------------------------------------------------------ DATA

/** @type {import("vue").Ref<string[]>} */
const intendedUseFilters = ref([]);
const productTypeFilter = ref('');
const showOnlyPrepackaged = ref(false);

// ------------------------------------------------------------ COMPUTED

const cEntity = computed(() => store.state.account.cEntity);

const articles = computed(
    () => store.state.catalog.articles
        .filter(
            a => a.status !== constants.articles.status.INACTIVE &&
                a.tags.some(t => t.code === 'CLEANING_PRODUCT')
        )
        .sort((a, b) => (a.extra?.sort_order || 0) - (b.extra?.sort_order || 0))
);

const filteredArticles = computed(() =>
{
    let out = articles.value;

    if(intendedUseFilters.value.length)
    {
        out = out.filter(a => intendedUseFilters.value.every(
            iufCode => a.tags.some(t => t.code === iufCode)
        ));
    }

    if(productTypeFilter.value)
    {
        out = out.filter(
            a => a.tags.some(t => t.code === productTypeFilter.value)
        );
    }

    if(showOnlyPrepackaged.value)
    {
        out = out.filter(
            a => a.tags.some(t => t.code === constants.tags.PREPACKAGED)
        );
    }

    return out;
});

const discountRules = computed(() => [
    {
        i18nNum:  1,
        num:      '1',
        discount: t('article_list.discounts.no_discount'),
    },
    {
        i18nNum:  2,
        num:      '2',
        discount: '2.5%',
    },
    {
        i18nNum:  3,
        num:      '3',
        discount: '5%',
    },
    {
        i18nNum:  5,
        num:      '4-5',
        discount: '7.5%',
    },
    {
        i18nNum:  7,
        num:      '6-7',
        discount: '10%',
    },
    {
        i18nNum:  9,
        num:      '8-9',
        discount: '12.5%',
    },
    {
        i18nNum:  10,
        num:      '≥10',
        discount: '15%',
    },
]);

// ------------------------------------------------------------ METHODS

function getIntendedUseFilterClass(tag)
{
    const classes = [
        `sl-article-grid-iu-filter--${tag.toLowerCase()}`, // e.g. "sl-article-grid-iu-filter--multi_pack"
    ];

    if(intendedUseFilters.value.includes(tag))
    {
        classes.push('sl-article-grid-iu-filter--active');
    }

    return classes;
}

function toggleIntendedUseFilter(tag)
{
    const index = intendedUseFilters.value.indexOf(tag);
    if(index === -1)
    {
        // Add tag to filters
        intendedUseFilters.value.push(tag);
    }
    else
    {
        // Remove tag from filters
        intendedUseFilters.value.splice(index, 1);
    }
};

function setProductTypeFilter(tag)
{
    if(tag === productTypeFilter.value)
    {
        productTypeFilter.value = '';
    }
    else
    {
        productTypeFilter.value = tag;
    }
};

function resetFilters()
{
    intendedUseFilters.value = [];
    productTypeFilter.value = '';
};

function hasArticlesWithTag(tag)
{
    return store.state.catalog.articles
        .filter(a => a.status !== constants.articles.status.INACTIVE)
        .some(a => a.tags.some(t => t.code === tag));
};
</script>
